import React, { useImperativeHandle, forwardRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Fade,
  Backdrop,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { colors } from "@zapalacorp/tokens";
import { Contact } from "Models/contact";
import { Timezone } from "Models/timezone";
import { TableListContacts } from "../TableListContacts";

export interface ContactListModalRef {
  open: (timezones?: Timezone[]) => void;
  close: () => void;
}

interface ContactListModalProps {
  onChange?: (contacts: Contact[]) => void;
  onClickEditContact?: (contact?: Contact) => void;
  contacts?: Contact[];
  timezones?: Timezone[];
}

export const ContactListModal = forwardRef<ContactListModalRef, ContactListModalProps>(
  ({ onChange, onClickEditContact, contacts = [], timezones = [] }, ref) => {
    const { t: translate } = useTranslation();

    const [open, setOpen] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
      open: () => {
        setOpen(true);
      },
      close: () => {
        handleClose();
      },
    }));

    const handleClose = (_event?: {}, reason?: string): void => {
      handleClean();
    };

    const handleCloseButton = (): void => {
      handleClean();
    };

    const handleClean = (): void => {
      setOpen(false);
    };

    const handleContactsChange = (updatedContacts: Contact[]) => {
      onChange?.(updatedContacts);
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        disableEscapeKeyDown
        aria-labelledby="contact-view-dialog-title"
        aria-describedby="contact-view-dialog-description"
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            minHeight: "600px",
          },
        }}
      >
        <DialogTitle
          id="contact-view-dialog-title"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pr: 1,
            pb: 0,
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              color: colors.gray600,
              fontSize: "22px",
              lineHeight: "28px",
            }}
          >
            {translate("contacts")}
          </Typography>

          <IconButton
            aria-label="Close modal"
            onClick={handleCloseButton}
            sx={{
              color: colors.gray600,
              "&:hover": {
                backgroundColor: colors.gray100,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            py: "16px !important",
            px: "24px !important",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <TableListContacts
              contacts={contacts}
              onChange={handleContactsChange}
              timezones={timezones}
              showHeaderGrid={true}
              viewOnly={false}
              showFullTable
              onClickEditContact={onClickEditContact}
            />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }
);

ContactListModal.displayName = "ContactListModal";
